import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  Col,
  Descriptions,
  Dropdown,
  Row,
  Space,
  Spin,
  Tabs,
  Tag,
  Typography,
  App,
  theme
} from 'antd';
import type { MenuProps } from 'antd';
import {
  ArrowLeftOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  CheckSquareOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  MoreOutlined,
  SyncOutlined,
  WarningOutlined,
  AlertOutlined
} from '@ant-design/icons';
import { trpc as api } from '@/lib/api';
import { useAuthStore } from '@/stores/authStore';
import { ChecklistProgress } from '@/components/work-order';
import { useWorkOrderTemplate } from '@/hooks/useWorkOrderTemplates';
import type { ChecklistItem } from '../../../shared/types/work-order-template.types';
import type { ChecklistResults } from '@shared/types/work-schedule.types';
import { WorkScheduleEditModal } from '@/components/WorkSchedule/EditModal';
import { WorkScheduleExecuteModal } from '@/components/WorkSchedule/ExecuteModal';
import type { WorkScheduleDetail } from '@/components/WorkSchedule/DetailModal';
import { UserDisplay, EquipmentDisplay, DateDisplay } from '@/components/common';
import { formatDateWithFallback } from '@/utils/dateFormat';
import { ApprovalButton } from '@/components/WorkSchedule/ApprovalButton';
import { ScheduleMediaGallery } from '@/components/schedule/ScheduleMediaGallery';
import { SchedulePartsSection } from '@/components/schedule/SchedulePartsSection';
import { ScheduleToolsSection } from '@/components/schedule/ScheduleToolsSection';
import { ScheduleCommentList } from '@/components/schedule/ScheduleCommentList';

const { Title, Paragraph, Text } = Typography;

const statusMeta: Record<
  string,
  { label: string; color: string; icon: React.ReactNode }
> = {
  scheduled: { label: '予定', color: 'blue', icon: <CalendarOutlined /> },
  in_progress: { label: '実行中', color: 'orange', icon: <SyncOutlined spin /> },
  pending_approval: { label: '承認待ち', color: 'gold', icon: <ClockCircleOutlined /> },
  completed: { label: '完了', color: 'green', icon: <CheckCircleOutlined /> },
  cancelled: { label: 'キャンセル', color: 'red', icon: <CloseCircleOutlined /> },
  postponed: { label: '延期', color: 'volcano', icon: <CalendarOutlined /> }
};

const IMPACT_LEVEL_COLORS = {
  critical: 'red',
  major: 'orange',
  minor: 'yellow'
} as const;

const IMPACT_LEVEL_LABELS = {
  critical: '重大',
  major: '大',
  minor: '小'
} as const;

const PRIORITY_COLORS = {
  low: 'default',
  medium: 'blue',
  high: 'orange',
  urgent: 'red'
} as const;

const PRIORITY_LABELS = {
  low: '低',
  medium: '中',
  high: '高',
  urgent: '緊急'
} as const;

const toChecklistResults = (value: unknown): ChecklistResults | undefined => {
  if (!value || Array.isArray(value)) {
    return undefined;
  }

  const candidate = value as Partial<ChecklistResults>;
  return Array.isArray(candidate.items) && typeof candidate.completionRate === 'number'
    ? (value as ChecklistResults)
    : undefined;
};

type EquipmentRow = {
  id?: string;
  equipmentId: string;
  equipmentName?: string | null;
  actualWorkNotes?: string | null;
  actualHours?: number | null;
};

type EquipmentSummary = {
  id: string;
  name?: string | null;
  displayId?: string | null;
  code?: string | null;
};

type UserSummary = {
  id: string;
  name?: string | null;
  email?: string | null;
  avatarUrl?: string | null;
  preferences?: Record<string, unknown> | null;
};

export default function ScheduleDetailPage(): JSX.Element {
  const { message } = App.useApp();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { token } = theme.useToken();
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [executeModalOpen, setExecuteModalOpen] = useState(false);
  const messageApi = message;

  const utils = api.useUtils();

  const {
    data: schedule,
    isLoading,
    refetch
  } = api.schedule.getById.useQuery(
    { id: id! },
    { enabled: Boolean(id) }
  );

  const { user } = useAuthStore();
  const effectiveCompanyId = schedule?.companyId ?? user?.companyId;

  // テンプレートからチェックリスト項目を取得
  const { template } = useWorkOrderTemplate(
    schedule?.workOrderTemplateId ?? null,
    { includeEquipments: false }
  );

  const equipmentListQueryInput = useMemo(() => {
    const base: { companyId?: string; page: number; limit: number } = {
      page: 1,
      limit: 200
    };
    if (effectiveCompanyId) {
      base.companyId = effectiveCompanyId;
    }
    return base;
  }, [effectiveCompanyId]);

  const userListQueryInput = useMemo(() => {
    const base: { companyId?: string; page: number; limit: number } = {
      page: 1,
      limit: 200
    };
    if (effectiveCompanyId) {
      base.companyId = effectiveCompanyId;
    }
    return base;
  }, [effectiveCompanyId]);

  const { data: equipmentQueryResult } = api.equipment.list.useQuery(
    equipmentListQueryInput,
    { enabled: Boolean(effectiveCompanyId) }
  );

  const { data: userQueryResult } = api.user.listDisplay.useQuery(
    userListQueryInput,
    {
    enabled: Boolean(effectiveCompanyId)
    }
  );

  const equipmentList = useMemo<EquipmentSummary[]>(() => {
    if (!equipmentQueryResult) {
      return [];
    }
    const fromData = (equipmentQueryResult as { data?: EquipmentSummary[] }).data;
    if (Array.isArray(fromData)) {
      return fromData;
    }
    const fromItems = (equipmentQueryResult as { items?: EquipmentSummary[] }).items;
    if (Array.isArray(fromItems)) {
      return fromItems;
    }
    return [];
  }, [equipmentQueryResult]);

  const getEquipmentLabel = (equipment: EquipmentSummary) =>
    equipment.name ?? equipment.displayId ?? equipment.code ?? equipment.id;

  const equipmentNameMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const equipment of equipmentList) {
      if (equipment?.id) {
        map.set(equipment.id, getEquipmentLabel(equipment));
      }
    }
    return map;
  }, [equipmentList]);

  const userList = useMemo<UserSummary[]>(() => {
    if (!userQueryResult) {
      return [];
    }
    const fromData = (userQueryResult as { data?: UserSummary[] }).data;
    if (Array.isArray(fromData)) {
      return fromData;
    }
    const fromItems = (userQueryResult as { items?: UserSummary[] }).items;
    if (Array.isArray(fromItems)) {
      return fromItems;
    }
    return [];
  }, [userQueryResult]);

  const getUserLabel = (userItem: UserSummary) =>
    userItem.name ?? userItem.email ?? userItem.id;

  const userNameMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const userItem of userList) {
      if (userItem?.id) {
        map.set(userItem.id, getUserLabel(userItem));
      }
    }
    return map;
  }, [userList]);

  // 担当者表示（assignedUsers または assignedTo から取得）
  const assignedUsersDisplay = useMemo(() => {
    // 1. assignedUsers配列がある場合
    if (schedule?.assignedUsers && schedule.assignedUsers.length > 0) {
      return (
        <Space size="small" wrap>
          {schedule.assignedUsers.map((u: UserSummary) => (
            <UserDisplay key={u.id} userId={u.id} user={u} size="small" />
          ))}
        </Space>
      );
    }
    // 2. assignedToフィールドがある場合（レガシー）
    if (schedule?.assignedTo) {
      const assignedUserInfo = userList.find(u => u.id === schedule.assignedTo);
      return (
        <UserDisplay
          userId={schedule.assignedTo}
          user={assignedUserInfo ? {
            id: schedule.assignedTo,
            name: assignedUserInfo.name ?? null,
            email: assignedUserInfo.email ?? null,
            avatarUrl: assignedUserInfo.avatarUrl ?? null,
            preferences: assignedUserInfo.preferences ?? null,
          } : undefined}
          size="small"
        />
      );
    }
    return <Text type="secondary">担当未設定</Text>;
  }, [schedule?.assignedUsers, schedule?.assignedTo, userList]);

  const checklistResults = useMemo<ChecklistResults | undefined>(
    () => toChecklistResults(schedule?.checklistResults ?? null),
    [schedule?.checklistResults]
  );

  const startWorkMutation = api.schedule.startWork.useMutation({
    onSuccess: () => {
      messageApi.success('作業を開始しました');
      void refetch();
    },
    onError: (error) => {
      messageApi.error(error.message || '作業開始に失敗しました');
    }
  });

  const completeWorkMutation = api.schedule.completeWork.useMutation({
    onSuccess: () => {
      messageApi.success('作業を完了しました');
      void refetch();
    },
    onError: (error) => {
      messageApi.error(error.message || '作業完了に失敗しました');
    }
  });

  const updateStatusMutation = api.schedule.updateStatus.useMutation({
    onSuccess: () => {
      messageApi.success('ステータスを更新しました');
      void refetch();
    },
    onError: (error) => {
      messageApi.error(error.message || 'ステータスの更新に失敗しました');
    }
  });

  const deleteMutation = api.schedule.delete.useMutation({
    onSuccess: () => {
      // 削除されたスケジュールのクエリキャッシュをリセットして、
      // ナビゲーション前の再フェッチによる404エラーを防止
      if (id) {
        utils.schedule.getById.reset({ id });
      }
      navigate('/app/maintenance');
      // ページ遷移後もトーストが表示されるようグローバルな message を使用
      message.success('スケジュールを削除しました');
    },
    onError: (error) => {
      messageApi.error(error.message || '削除に失敗しました');
    }
  });

  const duplicateMutation = api.schedule.duplicate.useMutation({
    onSuccess: (data) => {
      message.success(`スケジュールを複製しました: ${data.title}`);
      // 複製したスケジュールの詳細ページへ遷移
      navigate(`/app/schedule/${data.id}`);
    },
    onError: (error) => {
      messageApi.error(error.message || 'スケジュールの複製に失敗しました');
    }
  });

  const handleStartWork = useCallback(() => {
    if (!id) return;
    startWorkMutation.mutate({ id });
  }, [id, startWorkMutation]);

  const handleCompleteWork = useCallback(() => {
    if (!id) return;
    completeWorkMutation.mutate({ id });
  }, [id, completeWorkMutation]);

  const handleCancelWork = useCallback(() => {
    if (!id) return;
    updateStatusMutation.mutate({ id, status: 'cancelled' });
  }, [id, updateStatusMutation]);

  const handleDelete = useCallback(() => {
    if (!id) return;
    deleteMutation.mutate({ id });
  }, [deleteMutation, id]);

  const handleDuplicate = useCallback(() => {
    if (!id) return;
    duplicateMutation.mutate({ id });
  }, [duplicateMutation, id]);

  const scheduleStatus = schedule?.status ?? 'scheduled';
  const statusInfo = statusMeta[scheduleStatus] ?? {
    label: scheduleStatus,
    color: 'default',
    icon: <CalendarOutlined />
  };
  const scheduleDetail = useMemo<WorkScheduleDetail | null>(() => {
    if (!schedule) {
      return null;
    }
    const assignedUsers = Array.isArray(schedule.assignedUsers)
      ? schedule.assignedUsers
          .filter((user) => Boolean(user?.id))
          .map((user) => {
            const typedUser = user as {
              id: string;
              name?: string | null;
              email?: string | null;
              avatarUrl?: string | null;
              preferences?: Record<string, unknown> | null;
              role?: string | null;
              assignedAt?: string | Date | null;
            };
            return {
              ...typedUser,
              assignedAt: typedUser.assignedAt ? new Date(typedUser.assignedAt) : null,
            };
          })
      : undefined;
    return {
      ...(schedule as unknown as WorkScheduleDetail),
      assignedUsers,
    };
  }, [schedule]);
  const createdByUserId = (schedule as { createdBy?: string | null } | null)?.createdBy;
  const updatedByUserId = (schedule as { updatedBy?: string | null } | null)?.updatedBy;

  const selectedEquipments = useMemo<EquipmentRow[]>(() => {
    const list = Array.isArray(schedule?.selectedEquipments)
      ? (schedule?.selectedEquipments as unknown as EquipmentRow[])
      : [];
    return list.map((item) => ({
      ...item,
      actualHours: item.actualHours !== undefined ? Number(item.actualHours) : undefined,
      equipmentName:
        item.equipmentName ?? equipmentNameMap.get(item.equipmentId) ?? null
    }));
  }, [schedule, equipmentNameMap]);

  // …ボタン用のメニュー
  const moreMenuItems: MenuProps['items'] = useMemo(() => [
    {
      key: 'duplicate',
      label: '複製',
      icon: <CopyOutlined />,
      onClick: handleDuplicate,
      disabled: duplicateMutation.isPending
    },
    { type: 'divider' as const },
    {
      key: 'delete',
      label: '削除',
      icon: <DeleteOutlined />,
      danger: true,
      onClick: handleDelete
    }
  ], [handleDelete, handleDuplicate, duplicateMutation.isPending]);

  return (
    <>
      <div style={{ padding: 24 }}>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          {/* ヘッダー */}
          <Space style={{ width: '100%', justifyContent: 'space-between' }}>
            <Space direction="vertical" size="small">
              <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
                戻る
              </Button>
              <Space align="center">
                <Title level={2} style={{ margin: 0 }}>
                  {schedule?.title ?? 'スケジュール詳細'}
                </Title>
                <Text type="secondary">{schedule?.displayId}</Text>
                <Tag color={statusInfo.color} icon={statusInfo.icon}>
                  {statusInfo.label}
                </Tag>
              </Space>
            </Space>
            <Space>
              <Button icon={<EditOutlined />} onClick={() => setEditModalOpen(true)}>
                編集
              </Button>
              {scheduleStatus !== 'completed' && scheduleStatus !== 'cancelled' && (
                <Button
                  icon={<CheckSquareOutlined />}
                  onClick={() => setExecuteModalOpen(true)}
                >
                  作業報告
                </Button>
              )}
              {scheduleStatus === 'scheduled' && (
                <Button
                  type="primary"
                  icon={<SyncOutlined />}
                  loading={startWorkMutation.isPending}
                  onClick={handleStartWork}
                >
                  作業開始
                </Button>
              )}
              {scheduleStatus === 'in_progress' && (
                <>
                  <Button
                    type="primary"
                    icon={<CheckCircleOutlined />}
                    loading={completeWorkMutation.isPending}
                    onClick={handleCompleteWork}
                  >
                    作業完了
                  </Button>
                  <Button
                    danger
                    icon={<CloseCircleOutlined />}
                    loading={updateStatusMutation.isPending}
                    onClick={handleCancelWork}
                  >
                    中止
                  </Button>
                </>
              )}
              <Dropdown menu={{ items: moreMenuItems }} placement="bottomRight">
                <Button icon={<MoreOutlined />} />
              </Dropdown>
            </Space>
          </Space>

          {isLoading ? (
            <Spin />
          ) : !schedule ? (
            <Alert
              type="warning"
              showIcon
              message="スケジュールが見つかりません"
            />
          ) : (
            <>
              {/* ヘッダー情報カード: 誰が・いつ・どこで・何を */}
              <Card
                style={{
                  background: token.colorBgElevated,
                  borderColor: token.colorBorderSecondary,
                  marginBottom: 16
                }}
              >
                <Descriptions
                  size="small"
                  column={{ xs: 1, sm: 2, md: 4 }}
                >
                  <Descriptions.Item label={<><ClockCircleOutlined /> いつ</>}>
                    <DateDisplay value={schedule.scheduledDate} showTime strong />
                    {schedule.scheduledDuration && (
                      <Text type="secondary" style={{ marginLeft: 4 }}>
                        ({schedule.scheduledDuration}分)
                      </Text>
                    )}
                  </Descriptions.Item>
                  <Descriptions.Item label="何を">
                    <Tag color="blue">
                      {(schedule as { workTypeCategory?: { categoryLabel?: string } }).workTypeCategory?.categoryLabel ?? schedule.workType}
                    </Tag>
                    <Tag color={PRIORITY_COLORS[schedule.priority as keyof typeof PRIORITY_COLORS] || 'default'}>
                      {PRIORITY_LABELS[schedule.priority as keyof typeof PRIORITY_LABELS] || schedule.priority}
                    </Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="どこで">
                    {selectedEquipments.length > 0 ? (
                      (() => {
                        const item = selectedEquipments[0];
                        const equipment = equipmentList.find(e => e.id === item.equipmentId);
                        return (
                          <EquipmentDisplay
                            equipment={{
                              id: item.equipmentId,
                              displayId: equipment?.displayId || equipment?.code || '',
                              name: item.equipmentName ?? equipment?.name ?? item.equipmentId,
                            }}
                          />
                        );
                      })()
                    ) : (
                      <Text type="secondary">未設定</Text>
                    )}
                  </Descriptions.Item>
                  <Descriptions.Item label="誰が">
                    {assignedUsersDisplay}
                  </Descriptions.Item>
                </Descriptions>
              </Card>

              {/* タブ構造 */}
              <Tabs
                defaultActiveKey="overview"
                items={[
                  {
                    key: 'overview',
                    label: '概要',
                    children: (
                      <Space direction="vertical" size={16} style={{ width: '100%' }}>
                        {/* 説明 */}
                        {schedule.description && (
                          <Alert
                            message="説明"
                            description={schedule.description}
                            type="info"
                            showIcon
                          />
                        )}

                        {/* 進捗カード */}
                        <Row gutter={[16, 16]}>
                          <Col xs={24} md={12}>
                            <Card title="チェックリスト" size="small" style={{ height: '100%' }}>
                              {template?.checklistItems && (template.checklistItems as ChecklistItem[]).length > 0 ? (
                                <ChecklistProgress
                                  templateItems={(template.checklistItems as ChecklistItem[]) || []}
                                  results={checklistResults}
                                />
                              ) : (
                                <Text type="secondary">チェックリストが設定されていません</Text>
                              )}
                            </Card>
                          </Col>
                          <Col xs={24} md={12}>
                            <Card title="実績時間" size="small" style={{ height: '100%' }}>
                              {(schedule.actualStart || schedule.actualEnd || schedule.actualDuration) ? (
                                <Descriptions column={1} size="small" bordered>
                                  {schedule.actualStart && (
                                    <Descriptions.Item label="開始">
                                      <DateDisplay value={schedule.actualStart} showTime />
                                    </Descriptions.Item>
                                  )}
                                  {schedule.actualEnd && (
                                    <Descriptions.Item label="終了">
                                      <DateDisplay value={schedule.actualEnd} showTime />
                                    </Descriptions.Item>
                                  )}
                                  {schedule.actualDuration && (
                                    <Descriptions.Item label="所要時間">
                                      {schedule.actualDuration}分
                                    </Descriptions.Item>
                                  )}
                                </Descriptions>
                              ) : (
                                <Text type="secondary">作業未開始</Text>
                              )}
                            </Card>
                          </Col>
                        </Row>

                        {/* 作業報告 */}
                        {(schedule.workPerformed || schedule.findings || schedule.recommendations || schedule.result) && (
                          <Card title="作業報告" size="small">
                            <Row gutter={[16, 12]}>
                              {schedule.result && (
                                <Col span={24}>
                                  <Space>
                                    <Text strong>作業結果:</Text>
                                    <Tag color={
                                      schedule.result === 'good' ? 'green' :
                                        schedule.result === 'attention_needed' ? 'orange' :
                                          schedule.result === 'repair_required' ? 'red' : 'default'
                                    }>
                                      {schedule.result === 'good' ? '良好' :
                                        schedule.result === 'attention_needed' ? '要注意' :
                                          schedule.result === 'repair_required' ? '修理必要' : schedule.result}
                                    </Tag>
                                  </Space>
                                </Col>
                              )}
                              {schedule.workPerformed && (
                                <Col xs={24} md={12}>
                                  <Text strong>作業内容:</Text>
                                  <Paragraph style={{ marginTop: 4, marginBottom: 0, whiteSpace: 'pre-wrap' }}>
                                    {schedule.workPerformed}
                                  </Paragraph>
                                </Col>
                              )}
                              {schedule.findings && (
                                <Col xs={24} md={12}>
                                  <Text strong>発見事項:</Text>
                                  <Paragraph style={{ marginTop: 4, marginBottom: 0, whiteSpace: 'pre-wrap' }}>
                                    {schedule.findings}
                                  </Paragraph>
                                </Col>
                              )}
                              {schedule.recommendations && (
                                <Col span={24}>
                                  <Text strong>推奨事項:</Text>
                                  <Paragraph style={{ marginTop: 4, marginBottom: 0, whiteSpace: 'pre-wrap' }}>
                                    {schedule.recommendations}
                                  </Paragraph>
                                </Col>
                              )}
                            </Row>
                          </Card>
                        )}

                        {/* 繰り返し設定 */}
                        {scheduleDetail?.recurrenceRule?.enabled && (
                          <Alert
                            type="success"
                            message="繰り返し設定"
                            description={`パターン: ${scheduleDetail.recurrenceRule.pattern} / 間隔: ${scheduleDetail.recurrenceRule.interval}`}
                            showIcon
                          />
                        )}

                        {/* 承認セクション（承認待ちの場合のみ） */}
                        {scheduleStatus === 'pending_approval' && scheduleDetail && (
                          <Card title="承認" size="small">
                            <ApprovalButton
                              schedule={scheduleDetail}
                              onApproved={refetch}
                              onRejected={refetch}
                            />
                          </Card>
                        )}

                        {/* 作業記録セクション */}
                        <Card title="写真・動画" size="small">
                          <ScheduleMediaGallery
                            scheduleId={schedule.id}
                            editable={scheduleStatus !== 'cancelled'}
                          />
                        </Card>

                        {/* 部品・消耗品セクション */}
                        <SchedulePartsSection
                          scheduleId={id!}
                          companyId={effectiveCompanyId}
                          hasTemplate={Boolean(schedule?.workOrderTemplateId)}
                        />

                        {/* ツールセクション */}
                        <ScheduleToolsSection
                          scheduleId={id!}
                          hasTemplate={Boolean(schedule?.workOrderTemplateId)}
                        />

                        {/* コメント・履歴セクション（AVS-202） */}
                        <ScheduleCommentList
                          scheduleId={id!}
                          editable={scheduleStatus !== 'cancelled'}
                          currentStatus={scheduleStatus as 'scheduled' | 'in_progress' | 'pending_approval' | 'completed' | 'cancelled' | 'postponed'}
                          onStatusChange={() => void refetch()}
                        />

                        {/* 最終更新 */}
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          最終更新: {formatDateWithFallback(schedule.updatedAt, { showTime: true })}
                        </Text>
                      </Space>
                    )
                  },
                  // 承認・設定タブ（条件付き）
                  ...((schedule.requiresApproval || schedule.scheduleType === 'emergency') ? [{
                    key: 'approval',
                    label: '承認・設定',
                    children: (
                      <Space direction="vertical" size={16} style={{ width: '100%' }}>
                        {/* 承認ワークフロー設定 */}
                        {(schedule.requiresApproval || (schedule.approverUserIds && schedule.approverUserIds.length > 0)) && (
                          <Card title="承認ワークフロー設定" size="small">
                            <Descriptions column={1} size="small">
                              <Descriptions.Item label="完了時に承認が必要">
                                {schedule.requiresApproval ? (
                                  <Tag color="orange">必要</Tag>
                                ) : (
                                  <Tag color="default">不要</Tag>
                                )}
                              </Descriptions.Item>
                              {schedule.approverUserIds && schedule.approverUserIds.length > 0 && (
                                <Descriptions.Item label="承認者">
                                  <Space wrap>
                                    {schedule.approverUserIds.map((userId) => (
                                      <Tag key={userId} color="blue">
                                        {userNameMap.get(userId) ?? userId}
                                      </Tag>
                                    ))}
                                  </Space>
                                </Descriptions.Item>
                              )}
                            </Descriptions>
                          </Card>
                        )}

                        {/* 緊急作業情報 */}
                        {schedule.scheduleType === 'emergency' && (
                          <Card
                            title={
                              <Space>
                                <AlertOutlined style={{ color: '#ff4d4f' }} />
                                <span>緊急作業情報</span>
                              </Space>
                            }
                            size="small"
                            style={{ borderLeft: '4px solid #ff4d4f' }}
                          >
                            <Descriptions column={1} size="small">
                              {schedule.impactLevel && (
                                <Descriptions.Item label="影響レベル">
                                  <Tag
                                    color={IMPACT_LEVEL_COLORS[schedule.impactLevel as keyof typeof IMPACT_LEVEL_COLORS]}
                                    icon={<WarningOutlined />}
                                  >
                                    {IMPACT_LEVEL_LABELS[schedule.impactLevel as keyof typeof IMPACT_LEVEL_LABELS]}
                                  </Tag>
                                </Descriptions.Item>
                              )}
                              {schedule.reportedAt && (
                                <Descriptions.Item label="報告日時">
                                  <DateDisplay value={schedule.reportedAt} showTime />
                                </Descriptions.Item>
                              )}
                              {schedule.reportedBy && (
                                <Descriptions.Item label="報告者">
                                  {userNameMap.get(schedule.reportedBy) ?? schedule.reportedBy}
                                </Descriptions.Item>
                              )}
                              {schedule.affectedAreas && (
                                <Descriptions.Item label="影響範囲">
                                  {schedule.affectedAreas}
                                </Descriptions.Item>
                              )}
                              {schedule.estimatedDowntime && (
                                <Descriptions.Item label="推定ダウンタイム">
                                  {schedule.estimatedDowntime}分
                                </Descriptions.Item>
                              )}
                              {schedule.emergencyNotes && (
                                <Descriptions.Item label="緊急メモ">
                                  <Paragraph style={{ marginBottom: 0, whiteSpace: 'pre-wrap' }}>
                                    {schedule.emergencyNotes}
                                  </Paragraph>
                                </Descriptions.Item>
                              )}
                            </Descriptions>
                          </Card>
                        )}
                      </Space>
                    )
                  }] : []),
                  {
                    key: 'details',
                    label: '詳細情報',
                    children: (
                      <Card title="基本情報" size="small">
                        <Descriptions column={{ xs: 1, sm: 2 }} size="small" bordered>
                          <Descriptions.Item label="表示ID">{schedule.displayId}</Descriptions.Item>
                          <Descriptions.Item label="スケジュール種別">{schedule.scheduleType}</Descriptions.Item>
                          <Descriptions.Item label="作業カテゴリ">
                            <Tag>{(schedule as { workTypeCategory?: { categoryLabel?: string } }).workTypeCategory?.categoryLabel ?? schedule.workType}</Tag>
                          </Descriptions.Item>
                          <Descriptions.Item label="優先度">
                            <Tag color={PRIORITY_COLORS[schedule.priority as keyof typeof PRIORITY_COLORS] || 'default'}>
                              {PRIORITY_LABELS[schedule.priority as keyof typeof PRIORITY_LABELS] || schedule.priority}
                            </Tag>
                          </Descriptions.Item>
                          <Descriptions.Item label="作成者">
                            {createdByUserId ? (
                              <UserDisplay userId={createdByUserId} size="small" />
                            ) : (
                              <Text type="secondary">不明</Text>
                            )}
                          </Descriptions.Item>
                          <Descriptions.Item label="最終編集者">
                            {updatedByUserId ? (
                              <UserDisplay userId={updatedByUserId} size="small" />
                            ) : (
                              <Text type="secondary">未編集</Text>
                            )}
                          </Descriptions.Item>
                          <Descriptions.Item label="作成日時">
                            <DateDisplay value={schedule.createdAt} showTime />
                          </Descriptions.Item>
                          <Descriptions.Item label="最終更新日時">
                            <DateDisplay value={schedule.updatedAt} showTime />
                          </Descriptions.Item>
                        </Descriptions>
                      </Card>
                    )
                  }
                ]}
              />

              <WorkScheduleEditModal
                open={editModalOpen}
                mode="edit"
                schedule={scheduleDetail}
                onClose={() => setEditModalOpen(false)}
                onSuccess={() => {
                  setEditModalOpen(false);
                  void refetch();
                }}
              />

              <WorkScheduleExecuteModal
                open={executeModalOpen}
                schedule={scheduleDetail}
                onClose={() => setExecuteModalOpen(false)}
                onCompleted={() => {
                  setExecuteModalOpen(false);
                  void refetch();
                }}
              />
            </>
          )}
        </Space>
      </div>
    </>
  );
}
